import React, { useState, useEffect } from 'react';
import {
  Check,
  Package,
  Save,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import adminService, {
  CategoryField,
  FieldGroup
} from '../../services/admin/adminService';

type Step = 1 | 2 | 3 | 4;

const CategoryBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams<{ categoryId: string }>();
  const isEditMode = !!categoryId;

  // Stepper
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  // Step 1: Category Data
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [savedCategoryId, setSavedCategoryId] = useState<number | null>(null);

  // Step 2: Dynamic Fields
  const [fields, setFields] = useState<CategoryField[]>([]);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingField, setEditingField] = useState<CategoryField | null>(null);
  const [fieldName, setFieldName] = useState('');
  const [fieldType, setFieldType] = useState<'text' | 'number' | 'boolean' | 'dropdown' | 'image' | 'currency'>('text');
  const [fieldRequired, setFieldRequired] = useState(false);
  const [fieldOptions, setFieldOptions] = useState<string[]>(['']);

  // Step 3: Field Groups
  const [groups, setGroups] = useState<FieldGroup[]>([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<FieldGroup | null>(null);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [selectedFieldsForGroup, setSelectedFieldsForGroup] = useState<number[]>([]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEditMode && categoryId) {
      loadCategoryData();
    }
  }, [categoryId]);

  const loadCategoryData = async () => {
    try {
      setLoading(true);
      const categories = await adminService.getAllCategories();
      const category = categories.find(c => c.category_id === Number(categoryId));

      if (category) {
        setCategoryName(category.category_name);
        setCategoryDescription(category.category_description || '');
        setIsActive(category.is_active === 1);
        setSavedCategoryId(category.category_id);

        // Load fields
        const fieldsData = await adminService.getCategoryFields(category.category_id);
        setFields(fieldsData);

        // Load groups
        const groupsData = await adminService.getFieldGroups(category.category_id);
        setGroups(groupsData);
      }
    } catch (error: any) {
      toast.error('Failed to load category data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) {
      toast.error('Category name is required');
      return;
    }

    try {
      setLoading(true);

      if (savedCategoryId) {
        // Update existing category
        await adminService.updateCategory({
          category_id: savedCategoryId,
          category_name: categoryName,
          description: categoryDescription,
          is_active: isActive
        });
        toast.success('Category updated successfully');
      } else {
        // Create new category
        const created = await adminService.addCategory({
          category_name: categoryName,
          description: categoryDescription,
          is_active: isActive
        });
        setSavedCategoryId(created.category_id);
        toast.success('Category created successfully');
      }

      setCompletedSteps(prev => new Set(prev).add(1));
      // setCurrentStep(2);
      navigate('/admin/categories');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  /* Unused functions
  const handleAddField = () => { ... };
  const handleEditField = (field: CategoryField) => { ... };
  const handleSaveField = async () => { ... };
  const handleDeleteField = async (fieldId: number) => { ... };
  const handleAddGroup = () => { ... };
  const handleEditGroup = (group: FieldGroup) => { ... };
  const handleSaveGroup = async () => { ... };
  const handleDeleteGroup = async (groupId: number) => { ... };
  const handleNext = () => { ... };
  const handleBack = () => { ... };
  const handleFinish = () => { ... };
  */

  const steps = [
    { number: 1, title: 'Category Info', icon: Package }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin/categories')}
          className="flex items-center gap-2 text-secondary-600 hover:text-secondary-900 mb-4"
        >
          <ArrowLeft size={20} />
          Back to Categories
        </button>
        <h1 className="text-3xl font-bold text-secondary-900">
          {isEditMode ? 'Edit Category' : 'Create New Category'}
        </h1>
        <p className="text-secondary-600 mt-1">Follow the steps to set up your category</p>
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {/* Step 1: Category Info */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-secondary-900 mb-4">Category Information</h2>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Category Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., Restaurants, Hotels, Gyms"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Description
              </label>
              <textarea
                value={categoryDescription}
                onChange={(e) => setCategoryDescription(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Brief description of this category"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Status
                </label>
                <div className="flex items-center h-full">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-secondary-700">Active</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                onClick={handleSaveCategory}
                disabled={loading}
                className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
              >
                <Save size={18} />
                {savedCategoryId ? 'Update Category' : 'Save Category'}
              </button>
            </div>
          </div>
        )}

        {/* Steps 2, 3, and 4 removed as per request */}
      </div>

      {/* Field and Group Modals removed */}
    </div>
  );
};

export default CategoryBuilder;
